import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { hasMerchantPermission } from "../../api/merchantAuthApi";
import {
  getTodayAvailability,
  updateTodayAvailability,
} from "../../api/todayAvailabilityApi";
import ModuleHeader from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./todays-listing.scss";

const formatCurrency = (value) => value === null || value === undefined
  ? "—"
  : `RM${Number(value).toFixed(2)}`;

export default function TodaysListingPage() {
  const { selectedOutlet } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [resetTime, setResetTime] = useState("12:00 AM");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const canUpdate = hasMerchantPermission("todays_availability_update");

  useEffect(() => {
    if (!selectedOutlet?.id) return;
    setLoading(true);
    setMessage("");
    setError("");
    getTodayAvailability(selectedOutlet.id)
      .then((data) => {
        setProducts(data.products || []);
        setSavedProducts(data.products || []);
        setResetTime(data.reset_time || "12:00 AM");
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [selectedOutlet?.id]);

  const updateProduct = (productId, changes) => {
    setMessage("");
    setError("");
    setProducts((current) => current.map((product) => (
      product.id === productId ? { ...product, ...changes } : product
    )));
  };

  const handleQuantityChange = (product, value) => {
    const soldQuantity = Number(product.sold_quantity || 0);
    const quantity = Math.max(
      soldQuantity,
      Number.parseInt(value || String(soldQuantity), 10) || soldQuantity
    );
    updateProduct(product.id, {
      daily_quantity: quantity,
      ...(quantity === 0 ? { available: false } : {}),
    });
  };

  const hasChanges = (product) => {
    const saved = savedProducts.find((item) => item.id === product.id);
    return saved && (
      Number(saved.daily_quantity) !== Number(product.daily_quantity)
      || Boolean(saved.available) !== Boolean(product.available)
    );
  };

  const save = async (product) => {
    if (product.available && Number(product.daily_quantity) < 1) {
      setError("Enter a daily quantity greater than 0 before making the product available.");
      return;
    }
    if (Number(product.daily_quantity) < Number(product.sold_quantity || 0)) {
      setError(`Daily quantity cannot be lower than the ${product.sold_quantity} item(s) already sold today.`);
      return;
    }

    setUpdatingId(product.id);
    setError("");
    setMessage("");
    try {
      const updated = await updateTodayAvailability(selectedOutlet.id, product.id, {
        daily_quantity: Number(product.daily_quantity),
        available: Boolean(product.available),
      });
      setProducts((current) => current.map((item) => item.id === product.id ? updated : item));
      setSavedProducts((current) => current.map((item) => item.id === product.id ? updated : item));
      setMessage(`${product.name} has been updated successfully.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <ModulePageShell className="todays-listing">
      <ModuleHeader
        bleed
        eyebrow="Daily Operations"
        title="Today's Availability"
        description="Set today's product availability and quantity for the selected outlet. The start time is recorded when the product is made available."
      />

      <section className="todays-listing__notice">
        <div className="todays-listing__notice-icon">↻</div>
        <div><strong>Daily reset at {resetTime}</strong><p>Products automatically become Not Available each new day. The last entered quantity remains as the next day&apos;s default.</p></div>
      </section>

      {error ? <div className="todays-listing__message todays-listing__message--error">{error}</div> : null}
      {message ? <div className="todays-listing__message todays-listing__message--success">{message}</div> : null}

      <section className="todays-listing__card">
        <div className="todays-listing__card-header">
          <div><h3>Product Availability</h3><p>Product details and prices are read-only. Daily quantity and availability apply to the selected outlet and current day.</p></div>
          <span className="todays-listing__count">{products.length} {products.length === 1 ? "Product" : "Products"}</span>
        </div>

        <div className="todays-listing__table">
          <div className="todays-listing__table-head"><span>Product Name</span><span>Price</span><span>Original Price</span><span>Daily Quantity</span><span>Availability</span><span>Start Time</span><span>Action</span></div>
          {products.map((product) => (
            <div className="todays-listing__table-row" key={product.id}>
              <div className="todays-listing__product">
                <span className="todays-listing__product-image">{product.image ? <img src={product.image} alt="" /> : "SB"}</span>
                <div><strong>{product.name || "Unnamed product"}</strong><small>{product.sold_quantity} sold today · Product details are managed under Outlet / Products.</small></div>
              </div>
              <span className="todays-listing__price">{formatCurrency(product.price)}</span>
              <span className="todays-listing__original-price">{formatCurrency(product.normal_price)}</span>
              <label className="todays-listing__quantity-field"><input disabled={!canUpdate} type="number" min={product.sold_quantity || 0} step="1" value={product.daily_quantity} onChange={(event) => handleQuantityChange(product, event.target.value)} /></label>
              <div className="todays-listing__availability">
                <button disabled={!canUpdate} className={`todays-listing__switch ${product.available ? "todays-listing__switch--active" : ""}`} type="button" role="switch" aria-checked={product.available} onClick={() => updateProduct(product.id, { available: !product.available })}><span className="todays-listing__switch-knob" /></button>
                <span className={`todays-listing__status ${product.available ? "todays-listing__status--available" : "todays-listing__status--unavailable"}`}>{product.available ? "Available" : "Not Available"}</span>
              </div>
              <span className="todays-listing__start-time">{product.available_start_time || "—"}</span>
              <div className="todays-listing__action"><button type="button" disabled={!canUpdate || !hasChanges(product) || updatingId === product.id} onClick={() => save(product)}>{updatingId === product.id ? "Saving..." : "Update"}</button></div>
            </div>
          ))}
          {loading ? <div className="todays-listing__message">Loading today&apos;s availability...</div> : null}
          {!loading && !products.length ? <div className="todays-listing__message">No active product is configured for this outlet.</div> : null}
        </div>

        <div className="todays-listing__footer-note"><strong>Daily access</strong><span>The quantity is remembered for the next day, but availability must be enabled again each day.</span></div>
      </section>
    </ModulePageShell>
  );
}
